import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useAxios from '../utils/useAxios';
import {
  FaCalendarAlt,
  FaClock,
  FaSignInAlt,
  FaSignOutAlt,
  FaIdBadge,
  FaPhone,
  FaBriefcase,
  FaEnvelope,
  FaDownload,
  FaSearch,
  FaBusinessTime,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillWave,
  FaInfoCircle,
  FaEye
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FiAlertTriangle,
  FiSunrise,
  FiSunset,
  FiArrowDownCircle,
  FiCoffee
} from 'react-icons/fi';
import { useSidebar } from '../context/SidebarContext';

const EmployeDetail = () => {
  const { id } = useParams();
  const [employe, setEmploye] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatut, setSelectedStatut] = useState('');
  const [search, setSearch] = useState('');
  const [hoursWorked, setHoursWorked] = useState({
    today: 0,
    week: 0,
    month: 0
  });
  const [stats, setStats] = useState({
    present: 0,
    retard: 0,
    absent: 0,
    justifie: 0,
    nonJustifie: 0
  });
  const { sidebarVisible } = useSidebar();
  const axiosInstance = useAxios();
  const [montantPenalite, setMontantPenalite] = useState(0);
  const [montantPrime, setMontantPrime] = useState(0); 

  // États pour le modal de détail du motif
  const [motifModal, setMotifModal] = useState({ show: false, name: '', motif: '' });
  const formatHoursAndMinutes = (decimalHours) => {
    if (decimalHours === undefined || decimalHours === null) return '0h 00min';

    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
  };

  const openMotifModal = (presence) => {
    setMotifModal({
      show: true,
      name: `${presence.employe_prenom} ${presence.employe_nom}`,
      motif: presence.motif_sortie,
    });
  };

  const closeMotifModal = () => {
    setMotifModal({ show: false, name: '', motif: '' });
  };



  const generateMonths = () => {
    const months = [];
    const date = new Date();
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(date.getFullYear(), date.getMonth() - i, 1);
      const value = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
      const label = monthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  const monthsList = generateMonths();

  const pointageTypes = [
    { value: '', label: 'Tous types' },
    { value: 'entree', label: 'Entrées' },
    { value: 'sortie', label: 'Sorties' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [employeResponse, hoursResponse] = await Promise.all([
          axiosInstance.get(`/pointage/employes/${id}/`, {
            params: {
              ...(selectedMonth && { month: selectedMonth }),
              ...(selectedType && { type_pointage: selectedType }),
              ...(selectedStatut && { statut: selectedStatut }),
            }
          }),
          axiosInstance.get(`/pointage/employes/${id}/heures-travaillees/`, {
            params: {
              ...(selectedMonth && { month: selectedMonth }),
            }
          })
        ]);

        setEmploye(employeResponse.data.employe);
        setMontantPenalite(employeResponse.data.montant_penalite || 0);
        setMontantPrime(employeResponse.data.montant_prime || 0); 

        setHoursWorked({
          today: hoursResponse.data.jour || 0,
          week: hoursResponse.data.semaine || 0,
          month: hoursResponse.data.mois || 0
        });

if (employeResponse.data.employe?.presences) {
  const presences = employeResponse.data.employe.presences;
  
  const retards = presences.filter(p => p.statut === "RETARD");
  const absents = presences.filter(p => p.statut === "ABSENT");
  
  const retardsJustifies = retards.filter(p => p.est_justifiee).length;
  const retardsNonJustifies = retards.filter(p => !p.est_justifiee).length;
  
  const absentsJustifies = absents.filter(p => p.est_justifiee).length;
  const absentsNonJustifies = absents.filter(p => !p.est_justifiee).length;

  setStats({
    present: presences.filter(p => p.statut === "PRESENT").length,
    retard: retards.length,
    absent: absents.length,
    // Gardez ces totaux globaux si vous en avez besoin ailleurs
    justifie: retardsJustifies + absentsJustifies,
    nonJustifie: retardsNonJustifies + absentsNonJustifies,
    // Ajoutez les décomptes détaillés
    retardsJustifies,
    retardsNonJustifies,
    absentsJustifies,
    absentsNonJustifies
  });
}
      } catch (err) {
        console.error("Erreur lors de la récupération des données", err);
        setError("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, selectedMonth, selectedType, selectedStatut]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.slice(0, 5); 
  };

  const filteredPresences = useMemo(() => {
    if (!employe?.presences) return [];

    let presences = [...employe.presences];

    if (selectedType) {
      presences = presences.filter(p => p.type_pointage === selectedType);
    }

    if (selectedStatut) {
      presences = presences.filter(p => p.statut === selectedStatut);
    }

    if (search) {
      const searchTerm = search.toLowerCase();
      presences = presences.filter(p => {
        const periode = p.periode?.toLowerCase() || '';
        const motif = p.motif_sortie?.toLowerCase() || '';
        const statut = p.statut?.toLowerCase() || '';

        return (
          periode.includes(searchTerm) ||
          motif.includes(searchTerm) ||
          statut.includes(searchTerm)
        );
      });
    }

    return presences;
  }, [employe?.presences, search, selectedType, selectedStatut]);

  const handleExportPDF = async () => {
    if (!selectedMonth) {
      alert("Veuillez sélectionner un mois avant d'exporter le PDF");
      return;
    }

    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`Rapport des pointages - ${employe.first_name} ${employe.last_name}`, 14, 20);

      doc.setFontSize(12);
      doc.text(`Mois: ${monthsList.find(m => m.value === selectedMonth)?.label}`, 14, 30);
      doc.text(`Type: ${selectedType ? pointageTypes.find(t => t.value === selectedType)?.label : 'Tous types'}`, 14, 40);
      doc.text(`Statut: ${selectedStatut || 'Tous statuts'}`, 14, 50);
      doc.text(`Heures travaillées ce mois: ${formatHoursAndMinutes(hoursWorked.month)}`, 14, 60);
      doc.text(`Prime: ${montantPrime.toLocaleString()} FCFA | Pénalité: ${montantPenalite.toLocaleString()} FCFA`, 14, 70);

      // Ajout des statistiques
      doc.text(`Statistiques: Présents (${stats.present}) | Retards (${stats.retard}) | Absents (${stats.absent})`, 14, 80);

      const tableColumn = ["Date", "Heure", "Type", "Période", "Statut", "Justifié", ];
      const tableRows = [];

      filteredPresences.forEach((p) => {
        tableRows.push([
          p.date || '-',
          formatTime(p.heure),
          p.type_pointage === "entree" ? "Entrée" : "Sortie",
          p.periode === "autre" ? "Exceptionnelle" : 
            p.periode === "matin" ? "Matin" : 
            p.periode === "apres_midi" ? "Après-midi" : 
            p.periode === "descente" ? "Descente" : 
            p.periode === "pause" ? "Pause" : p.periode || '-',
          p.statut || '-',
          (p.statut === "RETARD" || p.statut === "ABSENT") ? (p.est_justifiee ? "Oui" : "Non") : '-',
         
        ]);
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 90,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [27, 1, 155] },
      });

      doc.save(`pointages_${employe.first_name}_${employe.last_name}_${selectedMonth}.pdf`);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      marginLeft: sidebarVisible ? 250 : 0,
      padding: '20px',
      minHeight: '100vh',
      backgroundColor: '#f4f6f9',
      fontFamily: 'Segoe UI, sans-serif',
      boxSizing: 'border-box',
      overflowX: 'auto',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px',
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '20px',
      color: '#1B019B',
      textDecoration: 'none',
      fontWeight: '600',
      padding: '8px 15px',
      borderRadius: '5px',
      transition: 'all 0.3s ease',
      ':hover': {
        backgroundColor: '#f0f0f0',
      },
    },
    title: {
      color: '#1B019B',
      fontWeight: '600',
      fontSize: '1.5rem',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      padding: '25px',
      marginBottom: '20px',
    },
    profileHeader: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: '25px',
      paddingBottom: '20px',
      borderBottom: '1px solid #eee',
      flexWrap: 'wrap',
    },
    profileImage: {
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      marginRight: '25px',
      objectFit: 'cover',
      border: '3px solid #f0f0f0',
    },
    profileInfo: {
      flex: 1,
    },
    profileName: {
      fontSize: '1.8rem',
      fontWeight: '600',
      marginBottom: '10px',
      color: '#333',
    },
    profileDetails: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '15px',
      marginTop: '15px',
      maxWidth: '100%',
    },    
    detailItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
      fontSize: '0.95rem',
    },
    detailIcon: {
      marginRight: '10px',
      color: '#1B019B',
      width: '20px',
      textAlign: 'center',
    },
    hoursSummary: {
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-around',
      flexWrap: 'wrap',
      gap: '10px'
    },
    hoursItem: {
      textAlign: 'center',
      padding: '10px',
      minWidth: '120px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    hoursValue: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#1B019B',
      marginTop: '5px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '20px',
    },
    tableHeader: {
      backgroundColor: '#1B019B',
      color: 'white',
      padding: '12px 15px',
      textAlign: 'left',
      fontWeight: '500',
    },
    tableRow: {
      borderBottom: '1px solid #eee',
      ':hover': {
        backgroundColor: '#f8f9fa',
      },
    },
    tableCell: {
      padding: '12px 15px',
      verticalAlign: 'middle',
    },
    justifiedCell: {
      fontWeight: 'bold',
      textAlign: 'center'
    },
    sectionTitle: {
      color: '#1B019B',
      fontWeight: '600',
      fontSize: '1.3rem',
      margin: '25px 0 15px 0',
      display: 'flex',
      alignItems: 'center',
    },
    loadingText: {
      textAlign: 'center',
      padding: '20px',
      color: '#666',
    },
    errorText: {
      textAlign: 'center',
      padding: '20px',
      color: '#dc3545',
      fontWeight: '500',
    },
    searchBar: {
      width: '100%',
      borderRadius: '20px',
      padding: '8px 15px',
      border: '1px solid #ccc',
      fontSize: '1rem',
    },
    selectFilter: {
      width: '100%',
      borderRadius: '20px',
      padding: '8px 15px',
      border: '1px solid #ccc',
      backgroundColor: 'white',
    },
    filtersContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '20px',
      width: '100%',
    },
    exportButton: {
      backgroundColor: '#1B019B',
      color: 'white',
      border: 'none',
      borderRadius: '20px',
      padding: '8px 15px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '0.9rem',
      marginLeft: 'auto',
      ':hover': {
        opacity: 0.9
      }
    },
    clearSearchButton: {
      background: 'none',
      border: 'none',
      color: '#dc3545',
      marginLeft: '-30px',
      cursor: 'pointer',
      fontSize: '1.2rem',
      padding: '0 5px'
    },
    statsBadge: {
      padding: '8px 15px',
      borderRadius: '4px',
      backgroundColor: '#f8f9fa',
      fontSize: '0.9rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px'
    },
    detailButton: {
      backgroundColor: '#1B019B',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '5px 10px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      gap: '5px'
    },
    fileLink: {
      color: '#1B019B',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '5px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Chargement en cours...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorText}>{error}</div>
      </div>
    );
  }

  if (!employe) {
    return (
      <div style={styles.container}>
        <div style={styles.errorText}>Employé non trouvé</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>

      {/* Modal pour afficher le détail du motif */}
      {motifModal.show && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={closeMotifModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Motif de sortie - {motifModal.name}
                </h5>
                <button className="btn-close" onClick={closeMotifModal}></button>
              </div>
              <div className="modal-body">
                <p
                  style={{
                    fontSize: '1rem',
                    textAlign: 'justify',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {motifModal.motif || 'Aucun motif fourni'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={styles.header}>
        <h1 style={styles.title}>Profil Employé</h1>
      </div>

      <div style={styles.card}>
        <div style={styles.profileHeader}>
          <img
            src={employe.image || '/default-user.png'}
            alt={`${employe.first_name} ${employe.last_name}`}
            style={styles.profileImage}
            onError={(e) => { e.target.src = '/default-user.png' }}
          />
          <div style={styles.profileInfo}>
            <div style={styles.profileName}>
              {employe.first_name} {employe.last_name}
            </div>

            <div style={styles.profileDetails}>
              <div style={styles.detailItem}>
                <span style={styles.detailIcon}><FaIdBadge /></span>
                <span><strong>Matricule:</strong> {employe.matricule || '-'}</span>
              </div>

              <div style={styles.detailItem}>
                <span style={styles.detailIcon}><FaBriefcase /></span>
                <span><strong>Poste:</strong> {employe.poste || '-'}</span>
              </div>

              <div style={styles.detailItem}>
                <span style={styles.detailIcon}><FaPhone /></span>
                <span><strong>Téléphone:</strong> {employe.telephone || '-'}</span>
              </div>

              <div style={styles.detailItem}>
                <span style={styles.detailIcon}><FaEnvelope /></span>
                <span><strong>Email:</strong> {employe.email || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.hoursSummary}>
          <div style={styles.hoursItem}>
            <div><FaBusinessTime /> Aujourd'hui</div>
            <div style={styles.hoursValue}>{formatHoursAndMinutes(hoursWorked.today)}</div>
          </div>
          <div style={styles.hoursItem}>
            <div><FaBusinessTime /> Cette semaine</div>
            <div style={styles.hoursValue}>{formatHoursAndMinutes(hoursWorked.week)}</div>
          </div>
          <div style={styles.hoursItem}>
            <div><FaBusinessTime /> Ce mois</div>
            <div style={styles.hoursValue}>{formatHoursAndMinutes(hoursWorked.month)}</div>
          </div>
          
          
          <div style={{
            ...styles.hoursItem,
            border: '2px solid #28a745',
            backgroundColor: '#d4edda',
            color: '#155724'
          }}>
            <div>
              <FaMoneyBillWave style={{ marginRight: '5px' }} /> Primes
            </div>
            <div style={{ ...styles.hoursValue, color: '#155724' }}>
              {montantPrime.toLocaleString()} FCFA
            </div>
          </div>
          
       
          <div style={{
            ...styles.hoursItem,
            border: '2px solid #dc3545',
            backgroundColor: '#f8d7da',
            color: '#dc3545'
          }}>
            <div>
              <FaBriefcase style={{ marginRight: '5px' }} /> Pénalités
            </div>
            <div style={{ ...styles.hoursValue, color: '#dc3545' }}>
              {montantPenalite.toLocaleString()} FCFA
            </div>
          </div>
        </div>

        <h3 style={styles.sectionTitle}>
          <FaCalendarAlt style={{ marginRight: '10px' }} />
          Historique des pointages ({filteredPresences?.length || 0})
        </h3>

        <div style={styles.filtersContainer}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FaSearch style={{ marginRight: '10px', color: '#1B019B' }} />
                <input
                  type="text"
                  placeholder="Filtrer par période, statut ou motif..."
                  style={styles.searchBar}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={styles.clearSearchButton}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FaCalendarAlt style={{ marginRight: '10px', color: '#1B019B' }} />
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={styles.selectFilter}
                >
                  <option value="">Tous les mois</option>
                  {monthsList.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FaClock style={{ marginRight: '10px', color: '#1B019B' }} />
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  style={styles.selectFilter}
                >
                  {pointageTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FiAlertTriangle style={{ marginRight: '10px', color: '#1B019B' }} />
                <select
                  value={selectedStatut}
                  onChange={(e) => setSelectedStatut(e.target.value)}
                  style={styles.selectFilter}
                >
                  <option value="">Tous statuts</option>
                  <option value="PRESENT">Présent</option>
                  <option value="RETARD">Retard</option>
                  <option value="ABSENT">Absent</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '15px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ ...styles.statsBadge, borderLeft: '4px solid #28a745' }}>
                Présents: <strong>{stats.present}</strong>
              </span>
              <span style={{ ...styles.statsBadge, borderLeft: '4px solid #ffc107' }}>
                Retards: <strong>{stats.retard}</strong>
                {stats.retard > 0 && (
                  <>
                    (<FaCheckCircle style={{ color: '#28a745', marginLeft: '5px' }} /> {stats.retardsJustifies || 0}
                    <FaTimesCircle style={{ color: '#dc3545', marginLeft: '5px' }} /> {stats.retardsNonJustifies || 0})
                  </>
                )}
              </span>
              <span style={{ ...styles.statsBadge, borderLeft: '4px solid #dc3545' }}>
                Absents: <strong>{stats.absent}</strong>
                {stats.absent > 0 && (
                  <>
                    (<FaCheckCircle style={{ color: '#28a745', marginLeft: '5px' }} /> {stats.absentsJustifies || 0}
                    <FaTimesCircle style={{ color: '#dc3545', marginLeft: '5px' }} /> {stats.absentsNonJustifies || 0})
                  </>
                )}
              </span>
            </div>

            <button
              style={{ 
                ...styles.exportButton,
                backgroundColor: loading || !selectedMonth ? '#ccc' : '#1B019B',
                cursor: loading || !selectedMonth ? 'not-allowed' : 'pointer'
              }}
              onClick={handleExportPDF}
              disabled={loading || !selectedMonth}
              title={!selectedMonth ? "Veuillez sélectionner un mois pour exporter" : ""}
            >
              <FaDownload /> {loading ? 'Génération...' : 'Exporter PDF'}
            </button>
          </div>
        </div>

        {filteredPresences && filteredPresences.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Date</th>
                  <th style={styles.tableHeader}>Heure</th>
                  <th style={styles.tableHeader}>Type</th>
                  <th style={styles.tableHeader}>Période</th>
                  <th style={styles.tableHeader}>Statut</th>
                  <th style={styles.tableHeader}>Motif</th>
                  <th style={styles.tableHeader}>Justifié</th>
                </tr>
              </thead>
              <tbody>
                {filteredPresences.map((presence) => (
                  <tr key={presence.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <FaCalendarAlt style={{ marginRight: '8px', color: '#666' }} />
                      {formatDate(presence.date)}
                    </td>
                    <td style={styles.tableCell}>
                      <FaClock style={{ marginRight: '8px', color: '#666' }} />
                      {formatTime(presence.heure)}
                    </td>
                    <td style={styles.tableCell}>
                      {presence.type_pointage === 'entree' ? (
                        <>
                          <FaSignInAlt style={{ marginRight: '8px', color: '#28a745' }} />
                          Entrée
                        </>
                      ) : (
                        <>
                          <FaSignOutAlt style={{ marginRight: '8px', color: '#dc3545' }} />
                          Sortie
                        </>
                      )}
                    </td>
                    <td>
                      {presence.periode === "autre" ? (
                        <span
                          title={presence.motif_sortie || 'Sortie hors période normale'}
                          className="text-warning fw-bold d-flex align-items-center gap-1"
                        >
                          <FiAlertTriangle /> Exceptionnelle
                        </span>
                      ) : presence.periode === "matin" ? (
                        <span className="text-primary fw-bold d-flex align-items-center gap-1">
                          <FiSunrise /> Matin
                        </span>
                      ) : presence.periode === "apres_midi" ? (
                        <span className="text-success fw-bold d-flex align-items-center gap-1">
                          <FiSunset /> Après-midi
                        </span>
                      ) : presence.periode === "descente" ? (
                        <span className="text-danger fw-bold d-flex align-items-center gap-1">
                          <FiArrowDownCircle /> Descente
                        </span>
                      ) : presence.periode === "pause" ? (
                        <span className="text-info fw-bold d-flex align-items-center gap-1">
                          <FiCoffee /> Pause
                        </span>
                      ) : (
                        <span className="fw-bold">
                          {presence.periode?.charAt(0).toUpperCase() + presence.periode?.slice(1).replace('_', ' ') || '-'}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${presence.statut === "PRESENT" ? 'bg-success' : presence.statut === "RETARD" ? 'bg-warning' : 'bg-danger'} rounded-pill`}>
                        {presence.statut}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      {presence.motif_sortie ? (
                        <button
                          style={styles.detailButton}
                          onClick={() => openMotifModal(presence)}
                        >
                          <FaInfoCircle /> Détail
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{...styles.tableCell, ...styles.justifiedCell}}>
                      {(presence.statut === "RETARD" || presence.statut === "ABSENT") ? (
                        <span className={`badge rounded-pill ${presence.est_justifiee ? 'bg-success' : 'bg-danger'}`}>
                          {presence.est_justifiee ? "Oui" : "Non"}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Aucune présence enregistrée pour cet employé avec les filtres actuels
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeDetail;